/**
 * «Whom we contacted» — the touch log.  Foundation for the meeting-notes branches (2026-10-31, migration 073).
 *
 * One row per time a staff member reached a person: a contact from the directory (contacts.js) or a member of the
 * register. The channel (phone, SMS, WhatsApp, e-mail, a visit, a meeting), when, a short note, and — for «we invited
 * them to this event» — the event. Staff only, never shown to the person, never sent anywhere. Kept until deleted by
 * hand (owner 2026-10-25, the rule for contacts and board data; retention.js category `touch_log`).
 *
 * Pure: no I/O, no clock — "now" is passed in.
 */
const { isId, text } = require('./fields');

const KINDS = ['contact', 'member'];
const CHANNELS = ['phone', 'sms', 'whatsapp', 'email', 'visit', 'meeting', 'other'];
const CHANNEL_NO = { phone: 'Telefon', sms: 'SMS', whatsapp: 'WhatsApp', email: 'E-post', visit: 'Besøk', meeting: 'Møte', other: 'Annet' };
const MAX_NOTE = 500;
/** Nothing before this is a real touch: a typo in the year, not a memory. */
const EARLIEST = '2000-01-01T00:00:00.000Z';
/** The fields staff may send. */
const FIELDS = ['channel', 'note', 'touchedAt', 'eventId'];

const label_no = (channel) => CHANNEL_NO[channel] || channel;

/** An ISO instant as the platform stores it, or null when `v` is not one. */
function instant(v) {
  if (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})$/.test(v)) return null;
  const t = Date.parse(v);
  return Number.isFinite(t) ? new Date(t).toISOString() : null;
}

/**
 * A touch as staff write it → { ok, value: { channel, note, touchedAt, eventId } } or { ok: false, errors }.
 * touchedAt defaults to `now` and may not be in the future; the note is at most MAX_NOTE characters; eventId is an id
 * or null (that the event exists is the caller's check). Unknown fields are refused, not ignored.
 */
function input(body, now) {
  const b = body && typeof body === 'object' && !Array.isArray(body) ? body : {};
  const errors = {};
  for (const k of Object.keys(b)) if (!FIELDS.includes(k)) errors[k] = 'Ukjent felt.';
  if (!CHANNELS.includes(b.channel)) errors.channel = 'Velg hvordan dere tok kontakt.';
  const note = text(b.note, 'notatet', MAX_NOTE, false);
  if (!note.ok) errors.note = note.message;
  const nowIso = instant(now instanceof Date ? now.toISOString() : now);
  if (!nowIso) throw new Error('touch-log.input: now must be an instant');
  let touchedAt = nowIso;
  if (b.touchedAt !== undefined && b.touchedAt !== null && b.touchedAt !== '') {
    const at = instant(b.touchedAt);
    if (!at) errors.touchedAt = 'Tidspunktet må være en dato med klokkeslett.';
    else if (at > nowIso) errors.touchedAt = 'Tidspunktet kan ikke være fram i tid.';
    else if (at < EARLIEST) errors.touchedAt = 'Tidspunktet er for langt tilbake.';
    else touchedAt = at;
  }
  let eventId = null;
  if (b.eventId !== undefined && b.eventId !== null && b.eventId !== '') {
    if (!isId(b.eventId)) errors.eventId = 'Ugyldig arrangement.';
    else eventId = b.eventId;
  }
  if (Object.keys(errors).length) return { ok: false, errors };
  return { ok: true, value: { channel: b.channel, note: note.value, touchedAt, eventId } };
}

/** The most recent touch of a list (by touchedAt), or null. The list may come in any order. */
function lastTouch(rows) {
  let best = null;
  for (const r of rows || []) if (r && r.touchedAt && (!best || r.touchedAt > best.touchedAt)) best = r;
  return best;
}

module.exports = { KINDS, CHANNELS, CHANNEL_NO, MAX_NOTE, FIELDS, EARLIEST, input, instant, label_no, lastTouch };
